import * as React from 'react';
import { createPortal } from 'react-dom';
import { Calendar } from 'primereact/calendar';
import { ListBox } from 'primereact/listbox';

import { CalendarClearFooter } from '../../../../components/synced/ui/CalendarClearFooter/CalendarClearFooter';
import { constantFormat } from '../../../../helpers/constants';
import { projectDocStatusFilterOptions } from '../../../../helpers/mapping/ProjectDocStatusFilterOptions';
import { projectDocTypeFilterOptions } from '../../../../helpers/mapping/ProjectDocTypeFilterOptions';
import styles from './ProjectDocsMobileFilterChips.module.css';
import { useState, useRef, useEffect } from 'react';
import clsx from 'clsx';
import { ProjectDocsMobileSearch } from './ProjectDocsMobileSearch';

type DropdownKey = `docType` | `status` | `created`;

interface FilterDropdownProps {
    label: React.ReactNode;
    active?: boolean;
    open: boolean;
    onToggle: () => void;
    onClose: () => void;
    children: React.ReactNode;
    hideDropdown?: boolean;
}

const FilterDropdown: React.FC<FilterDropdownProps> = ({
    label,
    active,
    open,
    onToggle,
    onClose,
    hideDropdown = false,
    children
}) => {
    const wrapperRef = useRef<HTMLDivElement | null>(null);
    const panelRef = useRef<HTMLDivElement | null>(null);
    const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);

    const computeCoords = () => {
        const rect = wrapperRef.current?.getBoundingClientRect();
        if (!rect) return null;
        const margin = 8;
        const viewportWidth = document.documentElement.clientWidth;
        const panelWidth = panelRef.current?.offsetWidth ?? 0;
        const maxLeft = window.scrollX + viewportWidth - panelWidth - margin;
        const left = Math.max(margin + window.scrollX, Math.min(rect.left + window.scrollX, maxLeft));
        return { top: rect.bottom + window.scrollY + 6, left };
    };

    useEffect(() => {
        if (!open) {
            setCoords(null);
            return;
        }
        setCoords(computeCoords());
        const onResize = () => setCoords(computeCoords());
        window.addEventListener(`resize`, onResize);
        return () => window.removeEventListener(`resize`, onResize);
    }, [open]);

    useEffect(() => {
        if (!open || !panelRef.current) return;
        const el = panelRef.current;
        const ro = new ResizeObserver(() => {
            setCoords((c) => {
                const next = computeCoords();
                if (!next) return c;
                if (c && c.left === next.left && c.top === next.top) return c;
                return next;
            });
        });
        ro.observe(el);
        return () => ro.disconnect();
    }, [open, coords]);

    useEffect(() => {
        if (!open) return;
        const onDocClick = (e: MouseEvent) => {
            const target = e.target as Node;
            if (wrapperRef.current?.contains(target)) return;
            if (panelRef.current?.contains(target)) return;
            onClose();
        };
        document.addEventListener(`mousedown`, onDocClick);
        return () => document.removeEventListener(`mousedown`, onDocClick);
    }, [open, onClose]);

    return (
        <div className={styles.dropdownWrapper} ref={wrapperRef}>
            <button type="button" className={`${styles.chip} ${active ? styles.chipActive : ``}`} onClick={onToggle}>
                {label}
                <i className="fa-solid fa-chevron-down" aria-hidden="true" />
            </button>
            {open &&
                coords &&
                createPortal(
                    <div
                        ref={panelRef}
                        className={hideDropdown ? '' : styles.dropdownPanel}
                        style={{ top: coords.top, left: coords.left }}
                    >
                        {children}
                    </div>,
                    document.body
                )}
        </div>
    );
};

export interface ProjectDocsMobileFilterChipsProps {
    projectTitleValue?: string;
    docTypeId?: number;
    statusIds?: number[];
    createdRange?: (Date | null)[] | null;
    onDocTypeChange: (v: number) => void;
    onProjectTitleChange: (v: string) => void;
    onStatusChange: (v: number[]) => void;
    onCreatedChange: (range: [Date, Date] | null) => void;
}

// value: 0 для ресета
const docTypeOptions = [{ label: `Все`, value: 0 }, ...projectDocTypeFilterOptions];
const statusOptions = [{ label: `Все`, value: 0 }, ...projectDocStatusFilterOptions];

export const ProjectDocsMobileFilterChips: React.FC<ProjectDocsMobileFilterChipsProps> = ({
    docTypeId,
    statusIds,
    createdRange,
    onDocTypeChange,
    onStatusChange,
    onCreatedChange,
    projectTitleValue,
    onProjectTitleChange
}) => {
    const [openDropdown, setOpenDropdown] = useState<DropdownKey | null>(null);
    const closeDropdown = () => setOpenDropdown(null);

    const [calendarValue, setCalendarValue] = useState<(Date | null)[] | null>(createdRange ?? null);
    const [showAdditionalFilters, setShowAdditionalFilters] = useState(false);

    React.useEffect(() => {
        setCalendarValue(createdRange ?? null);
    }, [createdRange]);

    const docTypeLabel = docTypeId
        ? (projectDocTypeFilterOptions.find((o) => o.value === docTypeId)?.label ?? `Вид документа`)
        : `Вид документа`;

    const statusId = statusIds?.[0];
    const statusLabel = statusId
        ? (projectDocStatusFilterOptions.find((o) => o.value === statusId)?.label ?? `Статус`)
        : `Статус`;

    const createdLabel =
        createdRange?.[0] && createdRange?.[1]
            ? `${constantFormat.DateToString(createdRange[0])} – ${constantFormat.DateToString(createdRange[1])}`
            : `Дата`;

    return (
        <div>
            <button
                className={clsx(styles.chip, {
                    [styles.chipActive]: showAdditionalFilters,
                    'mb-3': showAdditionalFilters
                })}
                onClick={() => setShowAdditionalFilters(!showAdditionalFilters)}
            >
                <i className="fa-solid fa-filter"></i>Расширенные фильтры
            </button>

            {showAdditionalFilters && (
                <div>
                    <ProjectDocsMobileSearch value={projectTitleValue ?? ''} onChange={onProjectTitleChange} />
                    <div className={styles.chipRow}>
                        <FilterDropdown
                            label={docTypeLabel}
                            active={!!docTypeId}
                            open={openDropdown === `docType`}
                            onToggle={() => setOpenDropdown((k) => (k === `docType` ? null : `docType`))}
                            onClose={closeDropdown}
                        >
                            <ListBox
                                className={styles.listbox}
                                value={docTypeId ?? 0}
                                options={docTypeOptions}
                                onChange={(e) => {
                                    onDocTypeChange(e.value);
                                    closeDropdown();
                                }}
                            />
                        </FilterDropdown>

                        <FilterDropdown
                            label={statusLabel}
                            active={!!statusId}
                            open={openDropdown === `status`}
                            onToggle={() => setOpenDropdown((k) => (k === `status` ? null : `status`))}
                            onClose={closeDropdown}
                        >
                            <ListBox
                                className={styles.listbox}
                                value={statusId ?? 0}
                                options={statusOptions}
                                onChange={(e) => {
                                    onStatusChange(e.value ? [e.value] : []);
                                    closeDropdown();
                                }}
                            />
                        </FilterDropdown>

                        <FilterDropdown
                            label={createdLabel}
                            active={!!createdRange?.[0]}
                            open={openDropdown === `created`}
                            onToggle={() => setOpenDropdown((k) => (k === `created` ? null : `created`))}
                            onClose={closeDropdown}
                            hideDropdown={true}
                        >
                            <Calendar
                                selectionMode="range"
                                readOnlyInput
                                locale="ru"
                                dateFormat={constantFormat.calendar}
                                value={calendarValue as any}
                                onChange={(e) => {
                                    const range = e.value as (Date | null)[];
                                    setCalendarValue(range ?? null);
                                    if (range?.[0] && range?.[1]) {
                                        onCreatedChange([range[0], range[1]]);
                                        closeDropdown();
                                    } else if (!range || (!range[0] && !range[1])) {
                                        onCreatedChange(null);
                                    }
                                }}
                                footerTemplate={() => (
                                    <CalendarClearFooter
                                        onClear={() => {
                                            setCalendarValue(null);
                                            onCreatedChange(null);
                                            closeDropdown();
                                        }}
                                    />
                                )}
                                inline
                                touchUI
                            />
                        </FilterDropdown>
                    </div>
                </div>
            )}
        </div>
    );
};
